import { useCallback, useEffect, useRef, useState } from 'react';

export interface CopyToClipboardResult {
  copy: (text: string) => Promise<boolean>;
  /** Alias kept for existing call sites. */
  copyToClipboard: (text: string) => Promise<boolean>;
  copied: boolean;
  error: Error | null;
}

/** Copies text, preferring the async Clipboard API with an execCommand fallback. */
export const useCopyToClipboard = (resetAfterMs = 2000): CopyToClipboardResult => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeout.current), []);

  const copy = useCallback(
    async (text: string) => {
      const succeeded = await writeText(text);
      setCopied(succeeded);
      setError(succeeded ? null : new Error('Copying to the clipboard was blocked.'));

      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => setCopied(false), resetAfterMs);

      return succeeded;
    },
    [resetAfterMs],
  );

  return { copy, copyToClipboard: copy, copied, error };
};

const writeText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    const succeeded = document.execCommand('copy');
    document.body.removeChild(textArea);
    return succeeded;
  } catch {
    return false;
  }
};

export default useCopyToClipboard;